import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { AdminBrandCollectionItemResponse } from '../responses/admin-brand-collection-item.response';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminBrandCreateRequest } from '../requests/admin-brand-create.request';
import { AdminBrandUpdateRequest } from '../requests/admin-brand-update.request';
import { AdminBrandDetailsResponse } from '../responses/admin-brand-details.response';

@Injectable({
  providedIn: 'root',
})
export class AdminBrandApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
    options?: {
      cacheable: boolean;
      skipByLoading: boolean;
    },
  ): Observable<CollectionResponse<AdminBrandCollectionItemResponse>> {
    const path = 'admin/cosmetic-brand';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBrandCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(id: Guid): Observable<AdminBrandDetailsResponse> {
    const path = `admin/cosmetic-brand/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(AdminBrandDetailsResponse)),
      );
  }

  public create(
    request: AdminBrandCreateRequest,
  ): Observable<CreateResourceResponse> {
    const path = `admin/cosmetic-brand`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBrandCreateRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)),
      );
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/cosmetic-brand/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  public update(id: Guid, request: AdminBrandUpdateRequest): Observable<void> {
    const path = `admin/cosmetic-brand/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBrandUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  protected hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
